import React, { createContext, useContext, useState, useCallback } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((item) => {
    setItems(prev => {
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex > -1) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + 1
        };
        return updated;
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    if (quantity < 1) {
      removeItem(itemId);
      return;
    }
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    ));
  }, [removeItem]);

  const updateHours = useCallback((itemId, hours) => {
    if (hours < 1) hours = 1;
    setItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, hours } : item
    ));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const getTotal = useCallback(() => {
    return items.reduce((total, item) => {
      // Skip quote-pending items from total
      if (item.quotePending) return total;
      // Base price for first hour
      let itemTotal = item.price * item.quantity;
      // Additional hours if applicable
      if (item.additionalHourPrice && item.hours > 1) {
        itemTotal += item.additionalHourPrice * (item.hours - 1) * item.quantity;
      }
      return total + itemTotal;
    }, 0);
  }, [items]);

  const hasQuotePending = items.some(item => item.quotePending);

  const itemCount = items.reduce((count, item) => count + item.quantity, 0);

  const value = {
    items,
    addItem,
    removeItem,
    updateQuantity,
    updateHours,
    clearCart,
    getTotal,
    hasQuotePending,
    itemCount,
    isOpen,
    setIsOpen,
    setDrawerOpen: setIsOpen,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
